import {
  Dispatch,
  RefObject,
  SetStateAction,
  useEffect,
  useState,
} from 'react'
import moment, { Moment } from 'moment'

import { isDemoPage } from '../../../../../utils/hooks'
import {
  DEMO_INVOICE_DATE_CHOOSE_EVENT,
  DEMO_INVOICE_DATE_OPEN_EVENT,
  DEMO_INVOICE_LOCATION_CHOOSE_EVENT,
  DEMO_INVOICE_LOCATION_SEARCH_EVENT,
} from '../../playback-constants'

type IncotermsOption = {
  disabled: boolean
  key: string
  value: string
}

export const getDemoIncotermsOptions = (options: IncotermsOption[]) => {
  if (!isDemoPage()) {
    return options
  }

  return options.map(option => ({
    ...option,
    disabled: false,
  }))
}

type UseDemoInvoiceDateParams = {
  initialDate: string
  setDate: Dispatch<SetStateAction<Moment | null>>
  setValue(name: string, value: string, options: Record<string, boolean>): void
}

export const useDemoInvoiceDate = ({
  initialDate,
  setDate,
  setValue,
}: UseDemoInvoiceDateParams) => {
  const isDemoMode = isDemoPage()
  const [ isOpen, setIsOpen ] = useState(false)

  useEffect(() => {
    if (!isDemoMode) {
      return
    }

    const handleOpen = () => {
      setDate(moment(initialDate))
      setIsOpen(true)
    }

    const handleChoose = () => {
      setDate(moment(initialDate))
      setValue('invoice_date', initialDate, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      setIsOpen(false)
    }

    window.addEventListener(DEMO_INVOICE_DATE_OPEN_EVENT, handleOpen)
    window.addEventListener(DEMO_INVOICE_DATE_CHOOSE_EVENT, handleChoose)

    return () => {
      window.removeEventListener(DEMO_INVOICE_DATE_OPEN_EVENT, handleOpen)
      window.removeEventListener(DEMO_INVOICE_DATE_CHOOSE_EVENT, handleChoose)
    }
  }, [ initialDate, isDemoMode, setDate, setValue ])

  return {
    onClose: isDemoMode ? () => setIsOpen(false) : undefined,
    open: isDemoMode ? isOpen : undefined,
  }
}

type UseDemoInvoiceLocationParams = {
  defaultCity?: LocationItem & { label: string }
  inputRef: RefObject<HTMLInputElement | null>
  locations: Array<LocationItem & { label: string }>
  locationRequest(params: { include_extra: boolean; q: string }): any
  onChoose(location: LocationItem & { label: string }): void
  onSearchComplete(): void
}

export const useDemoInvoiceLocation = ({
  defaultCity,
  inputRef,
  locations,
  locationRequest,
  onChoose,
  onSearchComplete,
}: UseDemoInvoiceLocationParams) => {
  const isDemoMode = isDemoPage()
  const [ inputValue, setInputValue ] = useState('')
  const [ isOpen, setIsOpen ] = useState(false)

  useEffect(() => {
    if (!isDemoMode) {
      return
    }

    const handleSearch = (event: Event) => {
      const query = (event as CustomEvent<{ query: string }>).detail?.query || ''

      inputRef.current?.focus({ preventScroll: true })
      setInputValue(query)
      setIsOpen(query.length >= 3)

      if (query.length >= 3) {
        locationRequest({ include_extra: true, q: query }).then(onSearchComplete)
      }
    }

    const handleChoose = () => {
      const targetLocation = locations.find(location => location.name === defaultCity?.name) || locations[0]

      if (!targetLocation) {
        return
      }

      onChoose(targetLocation)
      setInputValue('')
      setIsOpen(false)
      inputRef.current?.blur()
    }

    window.addEventListener(DEMO_INVOICE_LOCATION_SEARCH_EVENT, handleSearch)
    window.addEventListener(DEMO_INVOICE_LOCATION_CHOOSE_EVENT, handleChoose)

    return () => {
      window.removeEventListener(DEMO_INVOICE_LOCATION_SEARCH_EVENT, handleSearch)
      window.removeEventListener(DEMO_INVOICE_LOCATION_CHOOSE_EVENT, handleChoose)
    }
  }, [
    defaultCity?.name,
    inputRef,
    isDemoMode,
    locationRequest,
    locations,
    onChoose,
    onSearchComplete,
  ])

  return {
    autocompleteProps: {
      defaultValue: isDemoMode ? undefined : defaultCity,
      inputValue: isDemoMode ? inputValue : undefined,
      onClose: isDemoMode ? () => setIsOpen(false) : undefined,
      open: isDemoMode ? isOpen : undefined,
    },
    isDemoMode,
    searchQuery: isDemoMode ? defaultCity?.name.slice(0, 3) : undefined,
  }
}
